<script lang="ts">
  import Icon from '../../shared/components/Icon.svelte';
  import { sound } from '../../shared/lib/audio';
  import { EDITION } from '../../shared/lib/edition';
  import { game } from '../../shared/lib/game.svelte';
  import { PALETTES } from '../../shared/lib/palettes';
  import { settings, type MotionPref } from '../../shared/lib/settings.svelte';
  import { toasts } from '../../shared/lib/toasts.svelte';

  const canVibrate = typeof navigator !== 'undefined' && 'vibrate' in navigator;

  let exportCode = $state('');
  let importText = $state('');
  let confirmReset = $state(false);

  const motionOptions: Array<{ id: MotionPref; label: string }> = [
    { id: 'system', label: 'Match device' },
    { id: 'reduced', label: 'Reduced' },
    { id: 'full', label: 'Full' },
  ];

  async function doExport() {
    exportCode = game.exportCode();
    try {
      await navigator.clipboard.writeText(exportCode);
      toasts.push({
        tone: 'neutral',
        badge: '✓',
        title: 'Save code copied',
        body: 'Paste it on another device to continue there.',
      });
    } catch {
      toasts.push({
        tone: 'neutral',
        badge: '↓',
        title: 'Save code ready',
        body: 'Select the code below and copy it.',
      });
    }
  }

  function doImport() {
    if (game.importCode(importText)) {
      importText = '';
      toasts.push({ tone: 'accent', badge: '✓', title: 'Save loaded' });
    } else {
      toasts.push({
        tone: 'danger',
        badge: '!',
        title: 'That code didn’t work',
        body: 'Check that you copied the whole code, with nothing extra.',
      });
    }
  }

  function doReset() {
    game.reset();
    confirmReset = false;
    toasts.push({
      tone: 'neutral',
      badge: '0',
      title: 'Progress reset',
      body: 'Back to zero. Your settings are unchanged.',
    });
  }
</script>

<div class="view">
  <header class="view-head">
    <h1>Settings</h1>
  </header>

  <section aria-labelledby="s-pal">
    <h2 id="s-pal" class="eyebrow">Palette</h2>
    <div class="palettes" role="radiogroup" aria-labelledby="s-pal">
      {#each PALETTES as p (p.id)}
        <button
          class="pal"
          role="radio"
          aria-checked={settings.palette === p.id}
          data-palette={p.id}
          onclick={() => (settings.palette = p.id)}
        >
          <span class="swatch" aria-hidden="true">
            <span class="sw-num">73</span>
            <span class="sw-ticks">
              <i></i><i class="p"></i><i></i><i class="t"></i><i></i><i class="f"></i><i></i>
            </span>
          </span>
          <span class="pal-text">
            <strong>{p.name}</strong>
            <small>{p.note}</small>
          </span>
          {#if settings.palette === p.id}<span class="tick"><Icon name="check" size={16} /></span
            >{/if}
        </button>
      {/each}
    </div>
  </section>

  <section aria-labelledby="s-feel">
    <h2 id="s-feel" class="eyebrow">Sound and feel</h2>
    <div class="rows">
      <label class="row" for="opt-sound">
        <span
          ><strong>Sound</strong><small>Rising tones between primes, a chord on each one.</small
          ></span
        >
        <input
          id="opt-sound"
          type="checkbox"
          class="switch"
          bind:checked={settings.sound}
          onchange={() => sound.unlock()}
        />
      </label>
      <label class="row" for="opt-volume" class:dim={!settings.sound}>
        <span><strong>Volume</strong></span>
        <input
          id="opt-volume"
          type="range"
          min="0"
          max="1"
          step="0.05"
          bind:value={settings.volume}
          disabled={!settings.sound}
        />
      </label>
      {#if canVibrate}
        <label class="row" for="opt-haptics">
          <span
            ><strong>Vibration</strong><small>A short buzz on primes and discoveries.</small></span
          >
          <input id="opt-haptics" type="checkbox" class="switch" bind:checked={settings.haptics} />
        </label>
      {/if}
      <div class="row">
        <span
          ><strong>Motion</strong><small>Reduced turns off slides, sparks and pulses.</small></span
        >
        <div class="seg" role="radiogroup" aria-label="Motion">
          {#each motionOptions as m (m.id)}
            <button
              role="radio"
              aria-checked={settings.motion === m.id}
              class:on={settings.motion === m.id}
              onclick={() => (settings.motion = m.id)}
            >
              {m.label}
            </button>
          {/each}
        </div>
      </div>
    </div>
  </section>

  <section aria-labelledby="s-save">
    <h2 id="s-save" class="eyebrow">Your save</h2>
    <p class="note">
      Progress saves on this device automatically. Use a save code to move it to another device.
    </p>
    <div class="save">
      <div class="save-col">
        <button class="btn" onclick={doExport}><Icon name="copy" size={16} /> Copy save code</button
        >
        {#if exportCode}
          <textarea id="export-code" readonly rows="3" onfocus={(e) => e.currentTarget.select()}
            >{exportCode}</textarea
          >
        {/if}
      </div>
      <div class="save-col">
        <textarea id="import-code" rows="3" placeholder="Paste a save code" bind:value={importText}
        ></textarea>
        <button class="btn" disabled={!importText.trim()} onclick={doImport}>Load save code</button>
      </div>
    </div>

    <div class="danger">
      {#if confirmReset}
        <p>This erases your points, medals and upgrades. It can't be undone.</p>
        <div class="actions">
          <button class="btn btn-danger" onclick={doReset}>Erase progress</button>
          <button class="btn" onclick={() => (confirmReset = false)}>Keep playing</button>
        </div>
      {:else}
        <button class="btn btn-danger" onclick={() => (confirmReset = true)}>Reset progress…</button
        >
      {/if}
    </div>
  </section>

  <footer class="about">
    <span>Integerney {EDITION.toUpperCase()}</span>
    <a href="../index.html">All versions</a>
  </footer>
</div>

<style>
  section {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .palettes {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 200px), 1fr));
    gap: var(--space-2);
  }

  .pal {
    position: relative;
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-2);
    padding-right: var(--space-4);
    border-radius: var(--radius);
    text-align: left;
    background: var(--bg-raised);
    color: var(--ink);
    border: 1px solid var(--line);
    transition:
      transform var(--t-fast) var(--ease-out),
      border-color var(--t) var(--ease-out);
  }
  .pal:active {
    transform: scale(0.98);
  }
  .pal[aria-checked='true'] {
    border-color: var(--accent);
    box-shadow: 0 0 0 1px var(--accent);
  }

  .swatch {
    width: 64px;
    height: 56px;
    flex: none;
    border-radius: 8px;
    background: var(--paper, none), var(--bg);
    border: 1px solid var(--line);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
  }
  .sw-num {
    font-family: var(--font-num);
    font-weight: var(--num-weight);
    font-size: 1.15rem;
    line-height: 1;
    color: var(--prime);
  }
  .sw-ticks {
    display: flex;
    gap: 4px;
    align-items: flex-end;
    height: 12px;
  }
  .sw-ticks i {
    width: 2px;
    height: 6px;
    background: var(--ink-3);
    border-radius: 1px;
  }
  .sw-ticks .p {
    height: 12px;
    background: var(--prime);
  }
  .sw-ticks .t {
    height: 12px;
    background: var(--twin);
  }
  .sw-ticks .f {
    height: 9px;
    background: var(--famous);
  }

  .pal-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .pal-text strong {
    font-size: var(--text-sm);
  }
  .pal-text small {
    font-size: var(--text-xs);
    color: var(--ink-2);
  }
  .tick {
    position: absolute;
    top: 8px;
    right: 8px;
    color: var(--accent);
  }

  .rows {
    display: flex;
    flex-direction: column;
    border-radius: var(--radius);
    border: 1px solid var(--line);
    background: var(--bg-raised);
  }
  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-4);
    padding: var(--space-3) var(--space-4);
    flex-wrap: wrap;
  }
  .row + .row {
    border-top: 1px solid var(--line);
  }
  .row > span {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1 1 200px;
  }
  .row strong {
    font-size: var(--text-sm);
  }
  .row small {
    font-size: var(--text-xs);
    color: var(--ink-2);
  }
  .row.dim {
    opacity: 0.5;
  }
  input[type='range'] {
    accent-color: var(--accent);
    width: min(200px, 100%);
  }

  .switch {
    appearance: none;
    width: 44px;
    height: 26px;
    border-radius: 999px;
    background: var(--bg-sunken);
    border: 1px solid var(--line);
    position: relative;
    cursor: pointer;
    flex: none;
    transition: background-color var(--t) var(--ease-out);
  }
  .switch::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--ink-3);
    transition:
      transform var(--t) var(--ease-spring),
      background-color var(--t) var(--ease-out);
  }
  .switch:checked {
    background: var(--accent);
    border-color: var(--accent);
  }
  .switch:checked::after {
    transform: translateX(18px);
    background: var(--on-accent);
  }

  .seg {
    display: inline-flex;
    padding: 3px;
    gap: 2px;
    border-radius: 999px;
    background: var(--bg-sunken);
    flex-wrap: wrap;
  }
  .seg button {
    padding: 6px 12px;
    border-radius: 999px;
    font-size: var(--text-xs);
    font-weight: 600;
    color: var(--ink-2);
  }
  .seg button.on {
    background: var(--bg-raised);
    color: var(--ink);
    box-shadow: 0 1px 3px rgb(0 0 0 / 0.12);
  }

  .note {
    font-size: var(--text-sm);
    color: var(--ink-2);
  }
  .save {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 260px), 1fr));
    gap: var(--space-3);
  }
  .save-col {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    align-items: flex-start;
    min-width: 0;
  }
  textarea {
    width: 100%;
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    border: 1px solid var(--line);
    background: var(--bg-sunken);
    color: var(--ink);
    resize: vertical;
    word-break: break-all;
  }

  .danger {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    align-items: flex-start;
    padding-top: var(--space-3);
  }
  .danger p {
    font-size: var(--text-sm);
    color: var(--danger);
  }
  .actions {
    display: flex;
    gap: var(--space-2);
    flex-wrap: wrap;
  }

  .about {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: var(--space-2);
    font-size: var(--text-xs);
    color: var(--ink-3);
    padding-top: var(--space-4);
    border-top: 1px solid var(--line);
  }
  .about a {
    color: var(--accent);
  }
</style>
